<script setup lang="ts">
import Dialog from '../Dialog/Dialog.vue';
import Comments from './Comments.vue';
import CommentCount from '../forms/CommentCount/CommentCount.vue';
import { HasCommentSummary } from './types';
import { EntityWithCommentType, PtwCommentDto } from '@/api/generator';
import { computed, ref, watchEffect } from 'vue';

const props = withDefaults(
  defineProps<{
    entity: HasCommentSummary;
    entityWithCommentType: EntityWithCommentType;
    disabled?: boolean;
    open?: boolean;
    readonly?: boolean;
    // Some views (e.g. the Track-It measurement preview) want the icon to disappear entirely
    // once there's nothing to show, rather than staying as a standing "add a comment" affordance.
    showCountAlways?: boolean;
  }>(),
  { showCountAlways: true },
);

const isDialogOpen = ref<boolean>(!!props.open);

watchEffect(() => {
  isDialogOpen.value = !!props.open;
});

const emits = defineEmits<{
  change: [comments: PtwCommentDto[] | undefined];
  'update:open': [open: boolean];
}>();

const id = computed(() => props.entity.commentEntityCompositeId ?? props.entity.id ?? undefined);

function toggleDialogOpen(open?: boolean) {
  isDialogOpen.value = open === undefined ? !isDialogOpen.value : open;
  emits('update:open', isDialogOpen.value);
}
</script>

<template>
  <slot name="default" :toggleDialogOpen="toggleDialogOpen" :isDialogOpen="isDialogOpen">
    <TextVBtn
      v-if="showCountAlways || entity.commentCount"
      icon
      :disabled="disabled"
      @click="toggleDialogOpen(true)"
    >
      <CommentCount
        :value="entity"
        :showAlways="showCountAlways"
        :class="{ disabled }"
        color="primary-darken-2"
      />
    </TextVBtn>
  </slot>

  <Dialog :modelValue="isDialogOpen" size="large" @update:modelValue="toggleDialogOpen">
    <template #title> {{ $t('Comments') }} </template>

    <template #text>
      <Comments
        v-if="id"
        :id="id"
        :entityWithCommentType="entityWithCommentType"
        :readonly="readonly"
        :preloadedComments="readonly ? entity.comments : undefined"
        @change="emits('change', $event)"
      />
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.disabled {
  opacity: 0.4;
}
</style>
